import { useEffect, useState } from 'preact/hooks';
import { DEFAULT_SETTINGS, SKY_KINDS, SKY_KIND_NAMES, kindOn, panel, selection, setSetting, settings, showKinds, showToast, simNow, skyBand } from '../state';
import { isIOSDevice } from '../live/install';
import { services } from '../app';
import { CULTURES, CultureId, isSharedCulture } from '../data/cultures';
import { CultureCredit } from './CultureInfo';
import { Icon } from './icons';
import { meteorPreview } from '../live/meteors';
import { satStatus, satTimeProblem } from '../live/satellites';
import { Segmented, Sheet, Toggle } from './chrome';
import { CameraHelpToggle } from './CameraHelp';
import { CAMERA_HELP_OFFERED } from '../data/camerahelp';
import { DarkCheckEntry } from './DarkCheck';
import { fmtRelative } from './format';

export interface Band { id: string; name: string; wavelength: string; blurb: string; credit: string; licence: string }
let bandsP: Promise<Band[]> | null = null;
export const loadBands = () =>
  (bandsP ??= fetch(`${import.meta.env.BASE_URL}tex/bands/manifest.json`).then((r) => (r.ok ? r.json() : []), () => []));

function BandPicker() {
  const [bands, setBands] = useState<Band[]>([]);
  useEffect(() => void loadBands().then(setBands), []);
  const cur = skyBand.value;
  const b = bands.find((x) => x.id === cur);
  if (!bands.length) return null;
  return (
    <div class="group">
      <div class="group-title">The sky in other light</div>
      <div class="band-row" role="radiogroup" aria-label="Kind of light">
        <button role="radio" aria-checked={!cur} class={`band-chip ${!cur ? 'on' : ''}`} onClick={() => (skyBand.value = null)}>Visible</button>
        {bands.map((x) => (
          <button key={x.id} role="radio" aria-checked={cur === x.id} class={`band-chip band-${x.id} ${cur === x.id ? 'on' : ''}`} onClick={() => (skyBand.value = x.id)}>{x.name}</button>
        ))}
      </div>
      <div class="row-sub pad-x">
        {b ? (
          <>
            <strong>{b.name}, {b.wavelength}.</strong> {b.blurb}
            <span class="band-credit"> {b.credit}.</span>
          </>
        ) : (
          'What the sky would look like if your eyes could see radio waves, microwaves, infrared or gamma rays.'
        )}
      </div>
    </div>
  );
}

/** Warmth in words, for the slider and Settings. */
export const warmthWords = (w: number) => (w < 0.25 ? 'a little warm' : w < 0.55 ? 'warm' : w < 0.8 ? 'very warm' : 'warmest');

/** Night vision, as the iPhone's Night Shift: on or off, and one colour temperature from less to more warm. */
function NightVision() {
  const s = settings.value;
  const w = s.nightWarmth ?? 0.5;
  return (
    <div class="group">
      <Toggle on={s.nightMode} onChange={(v) => setSetting('nightMode', v)} icon="nightEye" label="Night vision" sub="Warms the screen’s colours, so your eyes stay used to the dark" />
      {s.nightMode && (
        <>
          <div class="slider-row warmth-row">
            <div class="slider-head"><label for="night-warmth">Colour temperature</label></div>
            <input
              id="night-warmth" class="warmth" type="range" min={0} max={100} step={1} value={Math.round(w * 100)} aria-valuetext={warmthWords(w)}
              onInput={(e) => setSetting('nightWarmth', Number((e.target as HTMLInputElement).value) / 100)}
            />
            <div class="warmth-ends" aria-hidden="true"><span>Less warm</span><span>More warm</span></div>
          </div>
          <div class="row-sub pad-x">The warmer the screen, the less it spoils your eyes’ adjustment to the dark. Turn your phone’s own brightness down too.</div>
          {isIOSDevice() && (
            <div class="row-sub pad-x">To warm the whole iPhone, including the clock and keyboard: Settings › Display &amp; Brightness › Night Shift.</div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * How the sky is shown: Full sky, everything Ahillah has (the default), or Naked eye, what the eye can see from here
 * tonight, named as far as room allows. Every layer below keeps its meaning in both; in Naked eye each shows only what
 * could be seen. Camera help, which checks the bright ones with the phone's camera, sits with them while it is offered.
 */
function SkyMode() {
  const eye = settings.value.nakedEye;
  return (
    <div class="group">
      <div class="eye-mode-switch">
        <Segmented label="How the sky is shown" value={eye ? 'eye' : 'full'} options={[['full', 'Full sky'], ['eye', 'Naked eye']]} onChange={(v) => setSetting('nakedEye', v === 'eye')} />
      </div>
      <p class="row-sub eye-mode-note">
        {eye ? 'Only what your eyes can see from here tonight, with their names (zoom in for more).' : 'Every star and object Ahillah knows. Choose Naked eye to see just what your eyes can see from here tonight.'}
      </p>
      <DarkCheckEntry />
      {CAMERA_HELP_OFFERED && <CameraHelpToggle />}
    </div>
  );
}

const STAR_NOTES = [
  'As many stars as your eyes can see from here.',
  'A few fainter stars too, to make the patterns easier to join up.',
  'Fainter stars still, more than your eyes can see from here, so the patterns stand out.',
];

/** Everything Layers can change, back as Ahillah starts (night vision is left as it is). */
function restoreStandardView() {
  const d = DEFAULT_SETTINGS;
  const keys = ['nakedEye', 'cameraHelp', 'constellationLines', 'constellationNames', 'constellationBounds', 'starNames', 'deepSky', 'milkyWay', 'grid', 'ecliptic', 'satellites', 'starlink', 'meteors', 'landscape', 'seeThroughGround', 'starBoost', 'twinkle', 'arabicNames', 'skyCulture', 'eyepiece', 'lunarStations', 'survey'] as const;
  settings.value = { ...settings.value, ...Object.fromEntries(keys.map((k) => [k, d[k]])) };
  skyBand.value = null;
  meteorPreview.value = false;
}

export function LayersPanel() {
  const s = settings.value;
  const sat = satStatus.value;
  const [confirm, setConfirm] = useState(false);
  const eye = s.nakedEye;
  // In Naked eye every star, planet and cluster the eye can see is named anyway: star names and galaxies are Full sky's.
  const kinds = eye ? SKY_KINDS.filter((k) => k !== 'starNames' && k !== 'deepsky') : SKY_KINDS;
  const all = kinds.every((k) => kindOn(s, k));
  const none = !kinds.some((k) => kindOn(s, k));
  return (
    <Sheet onClose={() => (panel.value = null)} size="tall" label="Sky view">
      <h2 class="sheet-title">Sky view</h2>
      <SkyMode />
      <NightVision />
      <div class="group">
        <div class="group-title">On the sky</div>
        <div class="band-row wrap" role="group" aria-label="On the sky">
          <button class={`band-chip ${all ? 'on' : ''}`} onClick={() => showKinds(kinds, true)}>Everything</button>
          {kinds.map((k) => (
            <button key={k} aria-pressed={kindOn(s, k)} class={`band-chip ${kindOn(s, k) ? 'on' : ''}`} onClick={() => showKinds([k], !kindOn(s, k))}>{SKY_KIND_NAMES[k]}</button>
          ))}
          <button class={`band-chip ${none ? 'on' : ''}`} onClick={() => showKinds(kinds, false)}>None</button>
        </div>
        <div class="row-sub pad-x">
          {eye
            ? 'Every star, planet and cluster your eyes can see is there, with names. Add any of these on top: each shows only what you could see.'
            : 'Stars, the planets and the Moon are always there. Pick any mix of the rest. More galaxies appear as you zoom in.'}
        </div>
      </div>
      <div class="group">
        <div class="group-title">Moving objects</div>
        <div class="seg-row">
          <span class="row-label-inline">Satellites</span>
          <Segmented label="Satellites" value={s.satellites} options={[['off', 'Off'], ['bright', 'Bright'], ['all', 'All']]} onChange={(v) => setSetting('satellites', v)} />
        </div>
        <div class="row-sub pad-x">
          {s.satellites === 'all'
            ? eye ? 'Every satellite your eyes could pick out from here, the fainter ones too.' : 'Every tracked satellite: faint ones show dimly, and those in Earth’s shadow in red.'
            : s.satellites === 'bright' ? 'Only satellites bright enough to see by eye.' : 'No satellites.'}
          {s.satellites === 'off' ? '' : (() => {
            // Why none show, when it is the orbit data's age or the time on show (as Tonight and the cards say).
            const problem = sat.count ? satTimeProblem(simNow()) : null;
            if (problem === 'old') return ' The saved orbits are too old to show where satellites are. Connect to the internet to update them.';
            if (problem === 'far') return ' Satellites show only within about two weeks of today.';
            return sat.updated ? ` Orbits updated ${fmtRelative(sat.updated)}.` : sat.source === 'snapshot' ? ' Using built-in orbits until fresh ones arrive.' : '';
          })()}
        </div>
        <Toggle on={s.starlink} onChange={(v) => setSetting('starlink', v)} icon="satellite" label="Starlink satellites"
          sub={s.satellites === 'off' ? 'Thousands of internet satellites; switch satellites on to see them' : s.satellites === 'bright' || eye ? 'Thousands of internet satellites; only the few you could see, such as a new ‘train’' : 'Thousands of internet satellites, most too faint to see by eye'} />
        <Toggle on={s.meteors} onChange={(v) => setSetting('meteors', v)} icon="meteor" label="Meteor showers"
          sub={eye ? 'Where each active shower’s meteors seem to come from, while that is above the horizon' : 'Where each active shower’s meteors seem to come from'} />
        <Toggle on={meteorPreview.value} onChange={(v) => (meteorPreview.value = v)} icon="sparkle" label="Practice meteors" sub="Pretend meteors, 40 times more often than real ones, to show where to look. Not real meteors" />
      </div>
      <div class="group">
        <div class="group-title">Stars</div>
        {eye ? (
          // How many stars is the eye's to say here (the darkness check sets it): a boost would bring back the stars it cannot see.
          <div class="row-sub eye-group-note">The stars your eyes can see from here, with their names (the brightest first; zoom in for more). For more stars, and to choose how many, switch to Full sky.</div>
        ) : (
          <>
            <div class="seg-row">
              <span class="row-label-inline">How many</span>
              <Segmented label="How many stars" value={s.starBoost} options={[[0, 'Natural'], [1, 'More'], [2, 'Most']]} onChange={(v) => setSetting('starBoost', v)} />
            </div>
            <div class="row-sub pad-x">{STAR_NOTES[s.starBoost] ?? STAR_NOTES[1]}</div>
            <Toggle on={s.starNames} onChange={(v) => setSetting('starNames', v)} icon="star" label="Star names" />
          </>
        )}
        <Toggle on={s.twinkle} onChange={(v) => setSetting('twinkle', v)} icon="sparkle" label="Twinkling" sub="Stronger near the horizon, as in the real sky" />
      </div>
      <div class="group">
        <div class="group-title">Constellations</div>
        <CulturePicker />
        <Toggle on={s.constellationLines} onChange={(v) => setSetting('constellationLines', v)} label="Lines" />
        <Toggle on={s.constellationNames} onChange={(v) => setSetting('constellationNames', v)} label="Names" />
        <Toggle on={s.constellationBounds} onChange={(v) => setSetting('constellationBounds', v)} label="Borders" sub="The official boundaries that divide up the whole sky" />
        {s.skyCulture === 'modern' && (
          <Toggle on={s.arabicNames} onChange={(v) => setSetting('arabicNames', v)} label="Names in Arabic" sub="The Arabic names of the modern constellations" />
        )}
        <Toggle on={s.lunarStations} onChange={(v) => setSetting('lunarStations', v)} label="Lunar stations (manāzil)" sub="The Moon’s 28 stations along its path, from the Arabic tradition" />
      </div>
      <div class="group">
        <div class="group-title">Galaxies and the Milky Way</div>
        {eye ? (
          <div class="row-sub eye-group-note">The clusters and galaxies your eyes can see from here, such as the Pleiades, are named on the sky. Others, and photographs of the sky, are in Full sky.</div>
        ) : (
          <Toggle on={s.deepSky} onChange={(v) => setSetting('deepSky', v)} icon="eye" label="Galaxies, clusters and nebulae" sub="The best-known ones; more appear as you zoom in" />
        )}
        <Toggle on={s.milkyWay} onChange={(v) => setSetting('milkyWay', v)} icon="sparkle" label="Milky Way" sub={eye ? 'Shows when the sky is dark enough to see it' : undefined} />
        {!eye && (
          <Toggle on={s.survey} onChange={(v) => setSetting('survey', v)} icon="camera" label="Sky photos when zoomed in" sub="Real photographs of the whole sky, loaded as you zoom in (needs a connection the first time)" />
        )}
      </div>
      <BandPicker />
      <div class="group">
        <div class="group-title">Guides</div>
        <div class="seg-row">
          <span class="row-label-inline">Grid</span>
          <Segmented label="Grid" value={s.grid} options={[['none', 'None'], ['altaz', 'Horizon'], ['equatorial', 'Equatorial']]} onChange={(v) => setSetting('grid', v)} />
        </div>
        <div class="row-sub pad-x">{s.grid === 'altaz' ? 'Lines of height above the horizon and compass direction.' : s.grid === 'equatorial' ? 'Lines of right ascension and declination, the sky’s own latitude and longitude.' : 'No grid.'}</div>
        <Toggle on={s.ecliptic} onChange={(v) => setSetting('ecliptic', v)} icon="sun" label="Ecliptic" sub="The path of the Sun, Moon and planets" />
        <div class="seg-row">
          <span class="row-label-inline">Eyepiece</span>
          <Segmented label="Eyepiece circle" value={s.eyepiece} options={[['none', 'Off'], ['bino7', '7×50'], ['bino10', '10×50'], ['scope', 'Scope']]} onChange={(v) => setSetting('eyepiece', v)} />
        </div>
        <div class="row-sub pad-x">A circle showing how much sky fits in binoculars or a small telescope, to help you find things. Zoom in to see the smaller circles.</div>
        <Toggle on={s.landscape} onChange={(v) => setSetting('landscape', v)} icon="eye" label="Hills and trees" sub="A landscape along the horizon" />
        <Toggle on={s.seeThroughGround} onChange={(v) => setSetting('seeThroughGround', v)} icon="eye" label="See through the ground" sub="Show what is below the horizon" />
      </div>
      {confirm ? (
        <div class="restore-confirm" role="group" aria-label="Restore the standard view">
          <p>Put everything in Sky view back to how Ahillah starts? Night vision stays as it is.</p>
          <div class="row-actions">
            <button class="btn-ghost" onClick={() => setConfirm(false)}>Cancel</button>
            <button class="btn" onClick={() => { restoreStandardView(); setConfirm(false); showToast('Standard view restored'); }}>Restore</button>
          </div>
        </div>
      ) : (
        <button class="show-more restore" onClick={() => setConfirm(true)}>Restore the standard view</button>
      )}
    </Sheet>
  );
}

/** Choose how constellations are drawn: modern, or the Arabian star groups. */
function CulturePicker() {
  const s = settings.value;
  const [open, setOpen] = useState(false);
  const [, force] = useState(0);
  const cur = CULTURES.find((c) => c.id === s.skyCulture) ?? CULTURES[0];
  const eng = services.value?.engine;
  useEffect(() => {
    if (eng && isSharedCulture(s.skyCulture) && eng.culture?.data.id !== s.skyCulture) void eng.ensureCulture(s.skyCulture).then(() => force((x) => x + 1));
  }, [s.skyCulture]);
  const choose = (id: CultureId) => {
    const sel = selection.peek();
    if (sel?.kind === 'figure' || sel?.kind === 'arabic') selection.value = null;
    setSetting('skyCulture', id);
    setOpen(false);
  };
  const loaded = eng?.culture && eng.culture.data.id === s.skyCulture ? eng.culture.data : null;
  return (
    <>
      <button class="list-row btn-row culture-current" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span class="row-text">
          <span class="row-label">Style: {cur.name}</span>
          <span class="row-sub">{cur.short}</span>
        </span>
        <Icon name={open ? 'chevronDown' : 'chevron'} size={16} />
      </button>
      {open && (
        <div class="culture-list" role="radiogroup" aria-label="Constellation style">
          {CULTURES.map((c) => (
            <button key={c.id} role="radio" aria-checked={c.id === s.skyCulture} class={`culture-row ${c.id === s.skyCulture ? 'on' : ''}`} onClick={() => choose(c.id)}>
              <span class="culture-dot" aria-hidden="true" />
              <span class="row-text">
                <span class="row-label">{c.name}</span>
                <span class="row-sub">{c.short}</span>
              </span>
            </button>
          ))}
        </div>
      )}
      {s.skyCulture === 'arabic' && (
        <div class="row-sub pad-x">The Arabic names of 116 stars and of the Arabs’ star groups.</div>
      )}
      {loaded && (
        <div class="pad-x">
          {loaded.intro.slice(0, 1).map((p, k) => <p class="row-sub" key={k}>{p}</p>)}
          <CultureCredit />
        </div>
      )}
    </>
  );
}
